import type React from 'react'
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react'

export type Theme = 'light' | 'dark' | 'system'

interface DarkModeContextType {
	darkMode: boolean
	toggleDarkMode: () => void
	theme: Theme
	setTheme: (t: Theme) => void
}

const DarkModeContext = createContext<DarkModeContextType | undefined>(
	undefined,
)

function getSystemPrefersDark() {
	return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export const DarkModeProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [theme, setThemeState] = useState<Theme>(() => {
		const saved = localStorage.getItem('theme')
		if (saved === 'light' || saved === 'dark' || saved === 'system')
			return saved
		// Migrate legacy boolean value
		const legacy = localStorage.getItem('darkMode')
		if (legacy === 'true') return 'dark'
		return 'light'
	})

	const [systemDark, setSystemDark] = useState(getSystemPrefersDark)

	// Listen for OS theme changes
	useEffect(() => {
		const mq = window.matchMedia('(prefers-color-scheme: dark)')
		const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches)
		mq.addEventListener('change', handler)
		return () => mq.removeEventListener('change', handler)
	}, [])

	const darkMode = theme === 'dark' || (theme === 'system' && systemDark)

	useEffect(() => {
		localStorage.setItem('theme', theme)
		// Clean up legacy key
		localStorage.removeItem('darkMode')
		if (darkMode) {
			document.documentElement.classList.add('dark')
		} else {
			document.documentElement.classList.remove('dark')
		}
	}, [theme, darkMode])

	const setTheme = useCallback((t: Theme) => setThemeState(t), [])
	const toggleDarkMode = useCallback(
		() => setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark')),
		[],
	)

	const value = useMemo(
		() => ({ darkMode, toggleDarkMode, theme, setTheme }),
		[darkMode, theme, toggleDarkMode, setTheme],
	)

	return (
		<DarkModeContext.Provider value={value}>
			{children}
		</DarkModeContext.Provider>
	)
}

export const useDarkMode = () => {
	const context = useContext(DarkModeContext)
	if (context === undefined) {
		throw new Error('useDarkMode must be used within a DarkModeProvider')
	}
	return context
}
